import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toISO, todayAdelaide, daysBetween, fmtDay } from './familyApi';

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const monthCells = (cursor) => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= total; d += 1) cells.push(toISO(new Date(cursor.getFullYear(), cursor.getMonth(), d)));
  return cells;
};

export const MonthNav = ({ cursor, setCursor, testPrefix }) => {
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  return (
    <div className="flex items-center justify-between mb-3">
      <button type="button" data-testid={`${testPrefix}-prev-month`} onClick={() => move(-1)} className="p-2 rounded-full hover:bg-amber-100 active:scale-95 transition-transform"><ChevronLeft size={20} /></button>
      <div data-testid={`${testPrefix}-month-label`} className="font-bold text-amber-900 text-base">
        {cursor.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
      </div>
      <button type="button" data-testid={`${testPrefix}-next-month`} onClick={() => move(1)} className="p-2 rounded-full hover:bg-amber-100 active:scale-95 transition-transform"><ChevronRight size={20} /></button>
    </div>
  );
};

export const useSwipe = (onLeft, onRight) => {
  const start = useRef(null);
  return {
    onTouchStart: (e) => { start.current = e.touches[0].clientX; },
    onTouchEnd: (e) => {
      if (start.current == null) return;
      const dx = e.changedTouches[0].clientX - start.current;
      if (dx < -50) onLeft();
      if (dx > 50) onRight();
      start.current = null;
    },
  };
};

const ModeToggle = ({ mode, setMode, setAnchor }) => (
  <div className="flex gap-2 mb-3" role="group">
    {[['range', 'Select a range'], ['tap', 'Tap single days']].map(([m, label]) => (
      <button key={m} type="button" data-testid={`calendar-mode-${m}`}
        onClick={() => { setMode(m); setAnchor(null); }}
        className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold border transition-colors ${mode === m ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-900 border-amber-300 hover:bg-amber-50'}`}>
        {label}
      </button>
    ))}
  </div>
);

const DayCell = ({ day, on, isAnchor, isToday, isPast, onTap }) => (
  <button type="button" data-testid={`calendar-day-${day}`} data-selected={on ? 'true' : 'false'} onClick={() => onTap(day)}
    className={`aspect-square rounded-lg text-sm font-semibold transition-colors select-none
      ${on ? 'bg-amber-500 text-white shadow-sm' : 'bg-white text-slate-700 hover:bg-amber-100'}
      ${isAnchor ? 'ring-4 ring-sky-400' : ''} ${isToday && !isAnchor ? 'ring-2 ring-amber-900' : ''} ${isPast && !on ? 'opacity-40' : ''}`}>
    {Number(day.slice(8))}
  </button>
);

export const AccessCalendar = ({ dates, onChange }) => {
  const [cursor, setCursor] = useState(() => new Date());
  const [mode, setMode] = useState('range');
  const [anchor, setAnchor] = useState(null);
  const sel = new Set(dates);
  const today = todayAdelaide();
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  const swipe = useSwipe(() => move(1), () => move(-1));

  const tap = (day) => {
    if (mode === 'tap') {
      const next = new Set(sel);
      if (next.has(day)) next.delete(day); else next.add(day);
      onChange([...next].sort());
      return;
    }
    if (!anchor) { setAnchor(day); return; }
    onChange([...new Set([...sel, ...daysBetween(anchor, day)])].sort());
    setAnchor(null);
    setMode('tap');
  };

  const hint = mode === 'tap'
    ? 'Tap a day to add or remove it.'
    : anchor ? `Start: ${fmtDay(anchor)} — now tap the last day.` : 'Tap the first day of the range.';

  return (
    <div data-testid="access-calendar" className="rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-3 sm:p-4">
      <ModeToggle mode={mode} setMode={setMode} setAnchor={setAnchor} />
      <MonthNav cursor={cursor} setCursor={setCursor} testPrefix="calendar" />
      <div {...swipe} className="touch-pan-y max-w-md mx-auto">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEK.map((w) => <div key={w} className="text-center text-[11px] font-bold uppercase text-amber-800/70">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthCells(cursor).map((day, i) => (day
            ? <DayCell key={day} day={day} on={sel.has(day)} isAnchor={anchor === day} isToday={day === today} isPast={day < today} onTap={tap} />
            : <div key={`e${i}`} />))}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-sm">
        <span data-testid="calendar-hint" className="text-amber-900">{hint}</span>
        <span data-testid="calendar-selected-count" className="font-bold text-amber-900 whitespace-nowrap">{dates.length} day{dates.length === 1 ? '' : 's'}</span>
      </div>
      <p className="mt-1 text-xs text-amber-800/70">Swipe left/right to change month · days run midnight–midnight Adelaide time</p>
    </div>
  );
};
